import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTop from '@/components/feature/BackToTop';
import PageMeta from '@/components/base/PageMeta';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    const currentCategory = categories.find((c) => c.slug === activeSlug);
    if (currentCategory) {
      setFilteredComics(comics.filter((c) => c.category === currentCategory.name));
    } else {
      setFilteredComics(comics);
    }
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const pageTitle = currentCategory
    ? `${currentCategory.name}漫画在线免费阅读 - 抖漫网页版`
    : '漫画分类 - 抖漫网页版';
  const pageDesc = currentCategory
    ? `${currentCategory.name}漫画专区：${currentCategory.description}。在抖漫网页版免费在线阅读全部${currentCategory.name}漫画，实时更新，流畅体验。`
    : '按类型浏览你喜欢的漫画：热血、恋爱、悬疑、搞笑、奇幻、科幻、古风、都市等多种分类，总有一款适合你。';

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentCategory?.name || '漫画'}漫画,抖漫网页版,免费漫画,在线漫画,${currentCategory?.name || ''}漫画在线阅读`}
        canonicalUrl={`https://douman.readdy.ai/category/${activeSlug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: pageDesc,
          url: `https://douman.readdy.ai/category/${activeSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '抖漫网页版',
            url: 'https://douman.readdy.ai',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://douman.readdy.ai' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: `https://douman.readdy.ai/category/${activeSlug}` },
            ],
          },
        }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">{t('article.breadcrumb_home')}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">{t('category.title')}</span>
            </nav>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('category.title')}
              </h1>
              <p className="text-sm text-foreground-600">{t('category.subtitle')}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {currentCategory && (
              <div className="p-4 rounded-xl bg-background-100/70 mb-8 flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground-950 mb-1">{currentCategory.name}漫画</h2>
                  <p className="text-sm text-foreground-600">{currentCategory.description}</p>
                </div>
              </div>
            )}

            <p className="text-sm text-foreground-500 mb-4">
              {t('category.comic_count', { count: filteredComics.length })}
            </p>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer no-underline"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - ${comic.category}漫画`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <span className="text-white text-xs font-medium">{t('home.read_now')}</span>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-semibold rounded-full">
                          {comic.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-500">{comic.author}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded-full font-medium">
                        {comic.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">{t('category.no_comics')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}